/**
 * Axis-tick formatting only.
 *
 * Everything the reader treats as a figure is formatted from the API's decimal string by
 * `@greencandle/api-client`. This one takes the float the chart already plotted, purely so
 * an axis can say "40K" instead of "40,000.00" - a tick label is a scale marker, not a
 * figure to reconcile against a statement.
 */
export function compactMoney(value: number, currency: string): string {
  const symbol = symbolFor(currency)
  const abs = Math.abs(value)
  const sign = value < 0 ? '-' : ''

  if (abs >= 1_000_000_000) {
    return `${sign}${symbol}${trim(abs / 1_000_000_000)}B`
  }
  if (abs >= 1_000_000) {
    return `${sign}${symbol}${trim(abs / 1_000_000)}M`
  }
  if (abs >= 1_000) {
    return `${sign}${symbol}${trim(abs / 1_000)}K`
  }
  return `${sign}${symbol}${abs.toFixed(abs < 10 ? 2 : 0)}`
}

function trim(value: number): string {
  // One decimal below 100 keeps 41.5K from collapsing to 42K; above it the digit is noise.
  return value < 100 ? value.toFixed(1).replace(/\.0$/, '') : value.toFixed(0)
}

function symbolFor(currency: string): string {
  try {
    const parts = new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).formatToParts(0)
    return parts.find((part) => part.type === 'currency')?.value ?? ''
  } catch {
    // An unknown code is still a code. Better on the axis than nothing.
    return `${currency} `
  }
}
